import type { NormalizedEvent } from "@/services/types/agent-gateway";
import type { AgentGatewayFailureKind } from "./agent-gateway-failure";
import { deduplicateEvents } from "./event-utils";

export const MAX_MESSAGE_LENGTH = 10_000;

export type OutgoingMessageAttempt = {
  idempotencyKey: string;
  text: string;
  submittedAt: string;
  afterEventId: string | null;
  requestKind?: "message" | "workflow_start";
  status: "pending" | "failed";
  errorMessage?: string;
  failureKind?: AgentGatewayFailureKind;
  retryable?: boolean;
};

export type StreamingMessage = {
  messageKey: string;
  text: string;
};

type CreateMessageAttemptOptions = {
  idempotencyKey?: string;
  submittedAt?: string;
  afterEventId?: string | null;
  requestKind?: "message" | "workflow_start";
};

export type ConversationTimelineItem =
  | {
      kind: "event";
      event: NormalizedEvent;
    }
  | {
      kind: "outgoing";
      attempt: OutgoingMessageAttempt;
    };

export function createMessageAttempt(
  text: string,
  {
    idempotencyKey = crypto.randomUUID(),
    submittedAt = new Date().toISOString(),
    afterEventId = null,
    requestKind,
  }: CreateMessageAttemptOptions = {},
): OutgoingMessageAttempt {
  return {
    idempotencyKey,
    text: text.trim(),
    submittedAt,
    afterEventId,
    ...(requestKind ? { requestKind } : {}),
    status: "pending",
  };
}

export function buildConversationTimeline(
  events: NormalizedEvent[],
  outgoingAttempts: OutgoingMessageAttempt[],
): ConversationTimelineItem[] {
  const timeline: ConversationTimelineItem[] = deduplicateEvents(events).map(
    (event) => ({
      kind: "event",
      event,
    }),
  );
  const attempts = [...outgoingAttempts].sort((first, second) =>
    first.submittedAt.localeCompare(second.submittedAt),
  );

  for (const attempt of attempts) {
    let insertionIndex = 0;

    if (attempt.afterEventId !== null) {
      const anchorIndex = timeline.findIndex(
        (item) =>
          item.kind === "event" && item.event.id === attempt.afterEventId,
      );

      if (anchorIndex >= 0) {
        insertionIndex = anchorIndex + 1;
      } else {
        const submittedAt = Date.parse(attempt.submittedAt);
        const firstLaterEventIndex = timeline.findIndex(
          (item) =>
            item.kind === "event" &&
            item.event.created_at !== null &&
            Date.parse(item.event.created_at) > submittedAt,
        );

        insertionIndex =
          firstLaterEventIndex >= 0 ? firstLaterEventIndex : timeline.length;
      }
    }

    while (timeline[insertionIndex]?.kind === "outgoing") {
      insertionIndex += 1;
    }

    timeline.splice(insertionIndex, 0, {
      kind: "outgoing",
      attempt,
    });
  }

  return timeline;
}

export function mergeNormalizedEvents(
  existing: NormalizedEvent[],
  incoming: NormalizedEvent[],
) {
  return deduplicateEvents([...existing, ...incoming]);
}

export function reconcileApprovalDecisionEvents(
  existing: NormalizedEvent[],
  incoming: NormalizedEvent[],
) {
  const decidedApprovalIds = new Set(
    incoming.flatMap((event) =>
      event.type === "approval_recorded" && "approval_id" in event.content
        ? [event.content.approval_id]
        : [],
    ),
  );
  const remainingEvents = existing.filter(
    (event) =>
      !(
        event.type === "approval_required" &&
        "approval_id" in event.content &&
        decidedApprovalIds.has(event.content.approval_id)
      ),
  );

  return mergeNormalizedEvents(remainingEvents, incoming);
}
